/**
 * Deteccion y resolucion de conflictos.
 *
 * Principio rector: un pago valido capturado offline NUNCA desaparece en silencio.
 * Si el servidor no puede aceptarlo tal cual, se conserva el dato local, la version
 * del servidor y la causa, y el caso pasa a una bandeja de revision. Nada monetario
 * se sobrescribe automaticamente.
 */

import type { Instant } from './clock.js';
import { assertDomain } from './errors.js';
import type { Ulid } from './ids.js';
import { formatMoney, type Money } from './money.js';

export const CONFLICT_CAUSES = [
  'VERSION_DESACTUALIZADA',
  'SALDO_INSUFICIENTE',
  'CARGO_YA_LIQUIDADO',
  'CONCEPTO_INACTIVO',
  'ESTUDIANTE_REASIGNADO',
  'ESTUDIANTE_INACTIVO',
  'CICLO_CERRADO',
  'USUARIO_REVOCADO',
  'DISPOSITIVO_REVOCADO',
  'IMPORTE_DUPLICADO_SOSPECHOSO',
  'CODIGO_ESTUDIANTE_DUPLICADO',
] as const;
export type ConflictCause = (typeof CONFLICT_CAUSES)[number];

export const CONFLICT_CAUSE_LABELS: Readonly<Record<ConflictCause, string>> = Object.freeze({
  VERSION_DESACTUALIZADA: 'El registro cambió en el servidor después de la captura',
  SALDO_INSUFICIENTE: 'El saldo del concepto ya no cubre el importe aplicado',
  CARGO_YA_LIQUIDADO: 'El concepto ya fue cubierto por otro pago',
  CONCEPTO_INACTIVO: 'El concepto fue desactivado',
  ESTUDIANTE_REASIGNADO: 'El estudiante cambió de grado o sección',
  ESTUDIANTE_INACTIVO: 'El estudiante fue dado de baja',
  CICLO_CERRADO: 'El ciclo escolar fue cerrado',
  USUARIO_REVOCADO: 'El usuario que capturó ya no tiene acceso',
  DISPOSITIVO_REVOCADO: 'El dispositivo que capturó fue revocado',
  IMPORTE_DUPLICADO_SOSPECHOSO: 'Existe otro pago idéntico muy cercano en el tiempo',
  CODIGO_ESTUDIANTE_DUPLICADO:
    'Otro dispositivo ya registró un estudiante con ese mismo código',
});

/**
 * Causas cuyo impacto es monetario. Estas SIEMPRE van a revision administrativa:
 * no se resuelven automaticamente ni siquiera si "parece" obvio (regla del contrato).
 */
const MONETARY_CAUSES: ReadonlySet<ConflictCause> = new Set<ConflictCause>([
  'SALDO_INSUFICIENTE',
  'CARGO_YA_LIQUIDADO',
  'IMPORTE_DUPLICADO_SOSPECHOSO',
]);

export const CONFLICT_STATUSES = ['ABIERTO', 'RESUELTO', 'DESCARTADO'] as const;
export type ConflictStatus = (typeof CONFLICT_STATUSES)[number];

export const RESOLUTION_ACTIONS = [
  'ACEPTAR_LOCAL', // se acepta el pago capturado offline tal cual
  'RECHAZAR_LOCAL', // se rechaza el pago; queda registrado y sin efecto en saldos
  'REGISTRAR_CORREGIDO', // se registra una operacion NUEVA con los datos corregidos
] as const;
export type ResolutionAction = (typeof RESOLUTION_ACTIONS)[number];

export const RESOLUTION_ACTION_LABELS: Readonly<Record<ResolutionAction, string>> = Object.freeze({
  ACEPTAR_LOCAL: 'Aceptar el pago tal como se capturó',
  RECHAZAR_LOCAL: 'Rechazar el pago (no afectará saldos)',
  REGISTRAR_CORREGIDO: 'Registrar una operación corregida',
});

export interface ConflictRecord {
  readonly id: Ulid;
  readonly institutionId: Ulid;
  readonly operationId: Ulid;
  readonly operationType: string;
  readonly cause: ConflictCause;
  readonly status: ConflictStatus;
  /** Evidencia inmutable: exactamente lo que el dispositivo envio. */
  readonly localSnapshot: unknown;
  /** Estado del servidor en el momento del rechazo. */
  readonly serverSnapshot: unknown;
  readonly monetaryImpact: Money | null;
  readonly detectedAt: Instant;
  readonly resolvedAt: Instant | null;
  readonly resolvedByUserId: Ulid | null;
  readonly resolutionAction: ResolutionAction | null;
  readonly resolutionNote: string | null;
}

export interface ConflictDetectionInput {
  readonly expectedVersion: number | null;
  readonly currentVersion: number;
  readonly studentActive: boolean;
  readonly cycleClosed: boolean;
  readonly conceptActive: boolean;
  readonly userRevoked: boolean;
  readonly deviceRevoked: boolean;
  readonly gradeChanged: boolean;
  readonly outstanding: Money;
  readonly requested: Money;
  readonly duplicateSuspected: boolean;
}

/**
 * Detecta la causa del conflicto. El orden importa: se reporta la causa RAIZ, la que
 * el administrador debe atender primero (acceso revocado antes que saldo, por ejemplo).
 * Devuelve `null` si la operacion puede aplicarse sin conflicto.
 */
export function detectConflict(input: ConflictDetectionInput): ConflictCause | null {
  if (input.userRevoked) return 'USUARIO_REVOCADO';
  if (input.deviceRevoked) return 'DISPOSITIVO_REVOCADO';
  if (input.cycleClosed) return 'CICLO_CERRADO';
  if (!input.studentActive) return 'ESTUDIANTE_INACTIVO';
  if (!input.conceptActive) return 'CONCEPTO_INACTIVO';
  // Un hecho monetario concreto pesa mas que una heuristica: si el cargo ya esta
  // liquidado, esa es la causa raiz que el administrador debe atender, aunque la
  // operacion tambien encaje en el patron de posible duplicado.
  if (input.outstanding.minor <= 0 && input.requested.minor > 0) return 'CARGO_YA_LIQUIDADO';
  if (input.requested.minor > input.outstanding.minor) return 'SALDO_INSUFICIENTE';
  if (input.duplicateSuspected) return 'IMPORTE_DUPLICADO_SOSPECHOSO';
  if (input.expectedVersion !== null && input.expectedVersion !== input.currentVersion) {
    return 'VERSION_DESACTUALIZADA';
  }
  if (input.gradeChanged) return 'ESTUDIANTE_REASIGNADO';
  return null;
}

/** `true` si el conflicto afecta dinero y por tanto exige intervencion humana. */
export function requiresAdminReview(cause: ConflictCause): boolean {
  return MONETARY_CAUSES.has(cause);
}

/**
 * Acciones seguras que se le ofrecen al administrador para una causa dada.
 * Nunca se ofrece "aceptar tal cual" cuando eso dejaria un saldo incoherente.
 */
export function availableActions(cause: ConflictCause): ResolutionAction[] {
  switch (cause) {
    case 'SALDO_INSUFICIENTE':
    case 'CARGO_YA_LIQUIDADO':
      // El importe ya no cabe donde se aplico: hay que redistribuirlo o rechazarlo.
      return ['REGISTRAR_CORREGIDO', 'RECHAZAR_LOCAL'];
    case 'IMPORTE_DUPLICADO_SOSPECHOSO':
      return ['ACEPTAR_LOCAL', 'RECHAZAR_LOCAL'];
    case 'CONCEPTO_INACTIVO':
    case 'ESTUDIANTE_INACTIVO':
    case 'CICLO_CERRADO':
      return ['REGISTRAR_CORREGIDO', 'RECHAZAR_LOCAL'];
    case 'VERSION_DESACTUALIZADA':
    case 'ESTUDIANTE_REASIGNADO':
      return ['ACEPTAR_LOCAL', 'REGISTRAR_CORREGIDO', 'RECHAZAR_LOCAL'];
    case 'USUARIO_REVOCADO':
    case 'DISPOSITIVO_REVOCADO':
      return ['ACEPTAR_LOCAL', 'RECHAZAR_LOCAL'];
    case 'CODIGO_ESTUDIANTE_DUPLICADO':
      // Nunca se ofrece "aceptar tal cual": aceptarlo crearia dos estudiantes con el
      // mismo codigo, o peor, fusionaria a dos personas distintas. Hay que corregir
      // el codigo o descartar el alta, y esa decision es humana.
      return ['REGISTRAR_CORREGIDO', 'RECHAZAR_LOCAL'];
    default:
      return ['RECHAZAR_LOCAL'];
  }
}

export interface ResolveConflictInput {
  readonly conflict: ConflictRecord;
  readonly action: ResolutionAction;
  readonly userId: Ulid;
  readonly at: Instant;
  readonly note: string | null;
}

/**
 * Resuelve un conflicto. La evidencia original (`localSnapshot`/`serverSnapshot`)
 * NO se modifica: la resolucion es una accion nueva que se agrega encima.
 */
export function resolveConflict(input: ResolveConflictInput): ConflictRecord {
  const { conflict, action } = input;
  assertDomain(
    conflict.status === 'ABIERTO',
    'CONFLICTO_REQUIERE_REVISION',
    'El conflicto ya fue resuelto',
    { conflictId: conflict.id },
  );
  assertDomain(
    availableActions(conflict.cause).includes(action),
    'CONFLICTO_REQUIERE_REVISION',
    `La acción "${RESOLUTION_ACTION_LABELS[action]}" no es válida para este conflicto`,
    { conflictId: conflict.id, cause: conflict.cause, action },
  );

  return Object.freeze({
    ...conflict,
    status: 'RESUELTO',
    resolvedAt: input.at,
    resolvedByUserId: input.userId,
    resolutionAction: action,
    resolutionNote: input.note?.trim().slice(0, 500) ?? null,
    // Se conservan intactos los snapshots originales.
    localSnapshot: conflict.localSnapshot,
    serverSnapshot: conflict.serverSnapshot,
  });
}

/**
 * Mensaje para el usuario de Caja: comprensible, sin detalles internos ni payloads.
 * El administrador ve la comparacion completa; el cajero solo el estado y que hacer.
 */
export function cashierMessage(cause: ConflictCause): string {
  switch (cause) {
    case 'SALDO_INSUFICIENTE':
    case 'CARGO_YA_LIQUIDADO':
      return 'Este pago quedó en revisión porque el saldo del estudiante cambió. Administración lo resolverá.';
    case 'ESTUDIANTE_INACTIVO':
    case 'ESTUDIANTE_REASIGNADO':
      return 'Este pago quedó en revisión porque los datos del estudiante cambiaron. Administración lo resolverá.';
    case 'USUARIO_REVOCADO':
    case 'DISPOSITIVO_REVOCADO':
      return 'Este pago quedó en revisión por un cambio de acceso. Comuníquese con Administración.';
    case 'IMPORTE_DUPLICADO_SOSPECHOSO':
      return 'Este pago quedó en revisión porque podría estar duplicado. Administración lo verificará.';
    case 'CODIGO_ESTUDIANTE_DUPLICADO':
      return 'El alta quedó en revisión: otro dispositivo usó el mismo código de estudiante.';
    default:
      return 'Este pago quedó en revisión. Administración lo resolverá.';
  }
}

/** Resumen para la bandeja del administrador: causa, impacto y acciones. */
export function conflictSummary(conflict: ConflictRecord): {
  causeLabel: string;
  impactLabel: string;
  actions: ResolutionAction[];
  requiresReview: boolean;
} {
  return {
    causeLabel: CONFLICT_CAUSE_LABELS[conflict.cause],
    impactLabel:
      conflict.monetaryImpact === null
        ? 'Sin impacto monetario directo'
        : `Impacto monetario: ${formatMoney(conflict.monetaryImpact)}`,
    actions: availableActions(conflict.cause),
    requiresReview: requiresAdminReview(conflict.cause),
  };
}
